import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { paramToSlug } from '../../utils';
import { StateSelect } from '../inputs';

interface AddressFieldType {
	label: string;
	value: string;
	setterFunction: (value: string) => void;
}

interface AddressInfoProps {
	street: string;
	street2: string | null;
	city: string;
	state: string;
	zip: string;
	addressFieldsAsArray: AddressFieldType[];
	isEditMode: boolean;
	setHasUnsavedChanges: (value: boolean) => void;
}

interface PayorAddressProps {
	payorStreet: string;
	payorCity: string;
	payorState: string;
	payorZip: string;
	setPayorStreet: (value: string) => void;
	setPayorCity: (value: string) => void;
	setPayorState: (value: string) => void;
	setPayorZip: (value: string) => void;
	isEditMode: boolean;
	setHasUnsavedChanges: (value: boolean) => void;
}

interface BillingAddressProps {
	billingStreet: string;
	billingAddressStreet2: string;
	billingCity: string;
	billingState: string;
	billingZip: string;
	setBillingStreet: (value: string) => void;
	setBillingAddressStreet2: (value: string) => void;
	setBillingCity: (value: string) => void;
	setBillingState: (value: string) => void;
	setBillingZip: (value: string) => void;
	isEditMode: boolean;
	setHasUnsavedChanges: (value: boolean) => void;
}

const formatAddress = (street: string, street2: string | null, city: string, state: string, zip: string) => {
	let formattedCity = city;
	if (city) {
		if (state) {
			formattedCity += ',';
		}
		formattedCity += ' ';
	}

	let formattedState = state;
	if (state) {
		formattedState += ' ';
	}

	const formatStreet2 = street2 ? `${street2}\n` : '';
	return `${street}\n${formatStreet2}${formattedCity}${formattedState}${zip}`;
};

const AddressInfo = ({
	street,
	street2,
	city,
	state,
	zip,
	addressFieldsAsArray,
	isEditMode,
	setHasUnsavedChanges,
}: AddressInfoProps) => {
	if (isEditMode) {
		return (
			<>
				{addressFieldsAsArray.map(({ label, value, setterFunction }) => {
					if (label.includes('State')) {
						return (
							<StateSelect
								key={label}
								label={label}
								value={value || ''}
								onChange={(e) => setterFunction(e.target.value)}
							/>
						);
					}
					return (
						<Grid key={label} item xs={2} marginTop='10px' marginBottom='10px'>
							<TextField
								label={label}
								fullWidth
								value={value}
								onChange={(e) => {
									setHasUnsavedChanges(true);
									setterFunction(e.target.value);
								}}
								inputProps={{
									'data-cy': `${paramToSlug(label)}-input`,
								}}
							/>
						</Grid>
					);
				})}
			</>
		);
	}
	return (
		<Grid item xs={2}>
			<TextField
				id='address-display'
				label='Address'
				fullWidth
				value={formatAddress(street, street2, city, state, zip)}
				multiline
				rows={3}
				InputProps={{
					readOnly: !isEditMode,
				}}
			/>
		</Grid>
	);
};

export const PayorAddress = ({
	payorStreet,
	payorCity,
	payorState,
	payorZip,
	setPayorStreet,
	setPayorCity,
	setPayorState,
	setPayorZip,
	isEditMode,
	setHasUnsavedChanges,
}: PayorAddressProps) => {
	const addressFields = [
		{ label: 'Payor Street Address', value: payorStreet, setterFunction: setPayorStreet },
		{ label: 'Payor City', value: payorCity, setterFunction: setPayorCity },
		{ label: 'Payor State', value: payorState, setterFunction: setPayorState },
		{ label: 'Payor ZIP', value: payorZip, setterFunction: setPayorZip },
	];

	return (
		<AddressInfo
			street={payorStreet}
			street2={null}
			city={payorCity}
			state={payorState}
			zip={payorZip}
			addressFieldsAsArray={addressFields}
			isEditMode={isEditMode}
			setHasUnsavedChanges={setHasUnsavedChanges}
		/>
	);
};

export const BillingAddress = ({
	billingStreet,
	billingAddressStreet2,
	billingCity,
	billingState,
	billingZip,
	setBillingStreet,
	setBillingAddressStreet2,
	setBillingCity,
	setBillingState,
	setBillingZip,
	isEditMode,
	setHasUnsavedChanges,
}: BillingAddressProps) => {
	const addressFields = [
		{ label: 'Street Address 1', value: billingStreet, setterFunction: setBillingStreet },
		{ label: 'Street Address 2', value: billingAddressStreet2, setterFunction: setBillingAddressStreet2 },
		{ label: 'City', value: billingCity, setterFunction: setBillingCity },
		{ label: 'State', value: billingState, setterFunction: setBillingState },
		{ label: 'ZIP', value: billingZip, setterFunction: setBillingZip },
	];

	return (
		<AddressInfo
			street={billingStreet}
			street2={billingAddressStreet2}
			city={billingCity}
			state={billingState}
			zip={billingZip}
			addressFieldsAsArray={addressFields}
			isEditMode={isEditMode}
			setHasUnsavedChanges={setHasUnsavedChanges}
		/>
	);
};
